/**
 * Email Subscriptions Master — controls every scheduled/automated email.
 *
 * Per email type: enabled toggle, IST send time, frequency (daily/weekly +
 * weekday), leadership role chips, department chips, explicit guaranteed
 * subscriber emails, exclusions, and a send-when-empty option.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CalendarClock, Save, RotateCcw, Loader2, Send, X, Plus, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut, apiPost } from '../../lib/api';

const WEEKDAYS = [
  { v: 'mon', label: 'Monday' }, { v: 'tue', label: 'Tuesday' }, { v: 'wed', label: 'Wednesday' },
  { v: 'thu', label: 'Thursday' }, { v: 'fri', label: 'Friday' }, { v: 'sat', label: 'Saturday' },
  { v: 'sun', label: 'Sunday' },
];
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EmailSubscriptions() {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [newEmail, setNewEmail] = useState({}); // { key: 'typing…' }
  const [departments, setDepartments] = useState([]);
  const [newExclusion, setNewExclusion] = useState({});
  const [preview, setPreview] = useState({});
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet('/settings/email-subscriptions');
      setData(r || {});
      setDirty(false);
    } catch (e) { toast.error('Failed to load email subscriptions'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    apiGet('/user-management/departments')
      .then(r => setDepartments(r?.departments || r?.items || (Array.isArray(r) ? r : [])))
      .catch(() => {});
  }, []);

  const update = (key, field, value) => {
    setDirty(true);
    setData((d) => ({ ...d, [key]: { ...(d[key] || {}), [field]: value } }));
  };

  const toggleDepartment = (key, dept) => {
    const cur = data[key]?.departments || [];
    update(key, 'departments', cur.includes(dept) ? cur.filter(d => d !== dept) : [...cur, dept]);
  };

  const addExclusion = (key) => {
    const e = (newExclusion[key] || '').trim().toLowerCase();
    if (!e) return;
    if (!EMAIL_RX.test(e)) { toast.error(`"${e}" is not a valid email address`); return; }
    const cur = data[key]?.excluded_emails || [];
    if (cur.includes(e)) { toast.warning('Already excluded'); return; }
    update(key, 'excluded_emails', [...cur, e]);
    setNewExclusion(m => ({ ...m, [key]: '' }));
  };

  const removeExclusion = (key, email) => {
    update(key, 'excluded_emails', (data[key]?.excluded_emails || []).filter(x => x !== email));
  };

  const previewRecipients = async (key) => {
    try {
      // POST preview uses the current (possibly unsaved) cfg for live feedback
      const r = await apiPost(`/notifications/digest/${key}/preview`, { cfg: data[key] || {} });
      setPreview(prev => ({ ...prev, [key]: r }));
    } catch { toast.error('Could not load preview'); }
  };

  const addRecipient = (key) => {
    const raw = (newEmail[key] || '').trim().toLowerCase();
    if (!raw) return;
    if (!EMAIL_RX.test(raw)) { toast.error(`"${raw}" is not a valid email address`); return; }
    const recips = data[key]?.recipients || [];
    if (recips.includes(raw)) { toast.warning('Already in the list'); return; }
    update(key, 'recipients', [...recips, raw]);
    setNewEmail((m) => ({ ...m, [key]: '' }));
  };

  const removeRecipient = (key, email) => {
    update(key, 'recipients', (data[key]?.recipients || []).filter((e) => e !== email));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await apiPut('/settings/email-subscriptions', data);
      setData(r || data);
      setDirty(false);
      toast.success('Email subscriptions saved');
      // Refresh any open previews so changes show immediately
      const openKeys = Object.keys(preview);
      for (const k of openKeys) {
        try {
          const pr = await apiPost(`/notifications/digest/${k}/preview`, { cfg: (r || data)[k] || {} });
          setPreview(prev => ({ ...prev, [k]: pr }));
        } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const sendAttendanceNow = async () => {
    setSendingNow(true);
    try {
      const r = await apiPost('/notifications/trigger/daily-attendance-digest');
      toast.success(r?.message || 'Attendance digest dispatched');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to dispatch digest');
    } finally { setSendingNow(false); }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <Loader2 size={20} className="animate-spin text-[#1F7A6E]" />
        <span className="ml-2 text-sm text-slate-500">Loading email subscriptions…</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="email-subscriptions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-[#1F7A6E]"><ArrowLeft size={18} /></button>
          <PageHeader title="Email Subscriptions" subtitle="Manage every automated email — attendance, tasks, digests" icon={<CalendarClock size={18} />} />
        </div>
        <button
          onClick={sendAttendanceNow}
          disabled={sendingNow}
          className="px-3 py-1.5 text-[10px] font-display font-bold uppercase tracking-wider border border-[#1F7A6E] text-[#1F7A6E] rounded-sm hover:bg-teal-50 disabled:opacity-50 flex items-center gap-1.5"
          data-testid="send-attendance-now-btn"
        >
          {sendingNow ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
          Send Attendance Digest Now
        </button>
      </div>

      <div className="space-y-4 mt-4">
        {Object.entries(data).map(([key, cfg]) => {
          return (
            <div key={key} className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`subscription-card-${key}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-display font-bold text-base">{cfg.label || key}</div>
                  <div className="text-[10px] font-mono text-slate-400">{key}</div>
                  {cfg.description && <p className="text-sm text-slate-600 mt-1">{cfg.description}</p>}
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <span className={`text-[10px] font-display font-bold uppercase tracking-wider ${cfg.enabled ? 'text-emerald-700' : 'text-slate-400'}`}>
                    {cfg.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!cfg.enabled}
                    onClick={() => update(key, 'enabled', !cfg.enabled)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${cfg.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                    data-testid={`enabled-toggle-${key}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${cfg.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} style={{ left: 0 }} />
                  </button>
                </label>
              </div>

              {/* Recipient explainer */}
              <p className="text-[11px] text-slate-500 mt-3 leading-relaxed" data-testid={`recipient-explainer-${key}`}>
                Recipients = <strong>Teams/Departments</strong> (whole teams) + <strong>Guaranteed Subscribers</strong> (specific emails), minus anyone in <strong>Exclusions</strong>.
              </p>

              <div className={`mt-4 grid grid-cols-1 md:grid-cols-12 gap-4 ${cfg.enabled ? '' : 'opacity-50 pointer-events-none'}`}>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1">Send Time (IST)</label>
                  <input
                    type="time"
                    value={cfg.send_time || '08:00'}
                    onChange={(e) => update(key, 'send_time', e.target.value)}
                    className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#1F7A6E] font-mono"
                    data-testid={`send-time-${key}`}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1">Frequency</label>
                  <select
                    value={cfg.frequency || 'daily'}
                    onChange={(e) => update(key, 'frequency', e.target.value)}
                    className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#1F7A6E]"
                    data-testid={`frequency-${key}`}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
                {cfg.frequency === 'weekly' && (
                  <div className="md:col-span-2">
                    <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1">Weekday</label>
                    <select
                      value={cfg.weekly_day || 'mon'}
                      onChange={(e) => update(key, 'weekly_day', e.target.value)}
                      className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#1F7A6E]"
                      data-testid={`weekly-day-${key}`}
                    >
                      {WEEKDAYS.map((w) => <option key={w.v} value={w.v}>{w.label}</option>)}
                    </select>
                  </div>
                )}
                <div className={cfg.frequency === 'weekly' ? 'md:col-span-6' : 'md:col-span-8'}>
                  <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1">
                    Guaranteed Subscribers <span className="normal-case font-normal text-slate-400">(always receive this email)</span>
                  </label>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {(cfg.recipients || []).map((email) => (
                      <span key={email} className="inline-flex items-center gap-1 bg-teal-50 text-[#1F7A6E] border border-[#1F7A6E]/30 rounded-full px-2.5 py-1 text-xs font-mono" data-testid={`recipient-chip-${key}-${email}`}>
                        {email}
                        <button type="button" onClick={() => removeRecipient(key, email)} className="hover:text-red-600" data-testid={`remove-recipient-${key}-${email}`}>
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                    <span className="inline-flex items-center gap-1">
                      <input
                        type="email"
                        value={newEmail[key] || ''}
                        onChange={(e) => setNewEmail((m) => ({ ...m, [key]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(key); } }}
                        placeholder="add email…"
                        className="border border-slate-300 rounded-sm px-2.5 py-1 text-xs font-mono focus:outline-none focus:border-[#1F7A6E] w-48"
                        data-testid={`add-recipient-input-${key}`}
                      />
                      <button type="button" onClick={() => addRecipient(key)} className="p-1.5 border border-slate-300 rounded-sm hover:border-[#1F7A6E] hover:text-[#1F7A6E]" data-testid={`add-recipient-btn-${key}`}>
                        <Plus size={12} />
                      </button>
                    </span>
                  </div>
                </div>

                <div className="md:col-span-4 flex items-end pb-1">
                  <label className="text-xs flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!cfg.send_when_empty}
                      onChange={(e) => update(key, 'send_when_empty', e.target.checked)}
                      className="w-3.5 h-3.5 accent-[#1F7A6E]"
                      data-testid={`send-when-empty-${key}`}
                    />
                    Send a short notice even when there is no data
                  </label>
                </div>
              </div>

              {/* ── Teams / Departments ────────────────────────────────── */}
              <div className="mt-4 border-t border-slate-100 pt-4">
                <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1.5">
                  Teams / Departments <span className="normal-case font-normal text-slate-400">(everyone in the selected department receives this)</span>
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {departments.map((dept) => {
                    const on = (cfg.departments || []).includes(dept);
                    return (
                      <button
                        key={dept}
                        type="button"
                        onClick={() => toggleDepartment(key, dept)}
                        className={`px-2.5 py-1 text-[10px] font-display font-bold uppercase tracking-wider rounded-full border transition-colors ${on ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-500 border-slate-300 hover:border-emerald-600'}`}
                        data-testid={`dept-chip-${key}-${dept.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {dept}
                      </button>
                    );
                  })}
                  {departments.length === 0 && <span className="text-xs text-slate-400">Loading departments…</span>}
                </div>
              </div>

              {/* ── Exclusions ──────────────────────────────────────────── */}
              <div className="mt-4 border-t border-slate-100 pt-4">
                <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1.5">
                  Exclude Specific People <span className="normal-case font-normal text-slate-400">(removed even if their role/department is selected)</span>
                </label>
                <div className="flex flex-wrap items-center gap-1.5">
                  {(cfg.excluded_emails || []).map((email) => (
                    <span key={email} className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2.5 py-1 text-xs font-mono">
                      {email}
                      <button type="button" onClick={() => removeExclusion(key, email)} className="hover:text-rose-900" data-testid={`excl-remove-${key}-${email}`}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                  {(cfg.excluded_emails || []).length === 0 && (
                    <span className="text-xs text-slate-400">No exclusions.</span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 mt-2">
                  <input
                    type="email"
                    value={newExclusion[key] || ''}
                    onChange={(e) => setNewExclusion(prev => ({ ...prev, [key]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExclusion(key); } }}
                    placeholder="exclude email…"
                    className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:border-rose-400 w-48"
                    data-testid={`excl-input-${key}`}
                  />
                  <button type="button" onClick={() => addExclusion(key)}
                    className="px-3 py-1.5 text-[10px] font-display font-bold uppercase tracking-wider border border-rose-400 text-rose-600 rounded-sm hover:bg-rose-50"
                    data-testid={`excl-add-btn-${key}`}>
                    Exclude
                  </button>
                </div>
              </div>

              {/* ── Live recipient preview ──────────────────────────────── */}
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => previewRecipients(key)}
                  className="text-[10px] font-display font-bold uppercase tracking-wider text-[#1F7A6E] underline hover:text-[#14504E]"
                  data-testid={`preview-${key}`}
                >
                  Preview who receives this →
                </button>
                {preview[key] && (
                  <div className="mt-1.5 text-xs bg-slate-50 border border-slate-200 rounded-sm px-3 py-2" data-testid={`preview-result-${key}`}>
                    <strong>{preview[key].recipient_count}</strong> recipient(s):{' '}
                    <span className="text-slate-600 font-mono">{preview[key].recipients.join(', ') || '— none —'}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button onClick={load} className="px-4 py-2 text-xs font-display font-bold uppercase tracking-wider border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1.5" data-testid="reset-btn">
          <RotateCcw size={11} /> Discard Changes
        </button>
        <button onClick={save} disabled={saving} className="px-4 py-2 text-xs font-display font-bold uppercase tracking-wider bg-[#1F7A6E] text-white rounded-sm hover:bg-[#14504E] disabled:opacity-50 flex items-center gap-1.5" data-testid="save-btn">
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
          {saving ? 'Saving…' : 'Save All'}
        </button>
      </div>
    </div>
  );
}
